import React, { createRef, useEffect, useState } from 'react'
import { graphql, Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import htmr from 'htmr'
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  PinterestIcon,
  PinterestShareButton,
  RedditIcon,
  RedditShareButton,
  TumblrIcon,
  TumblrShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share'
import slugify from 'slugify'
import styled from 'styled-components'

import Layout from '../components/core/layout'
import SEO from '../components/core/seo'
import BlogLinkWBgPicture from '../components/elements/BlogLinkWBgPicture'
import Card from '../components/elements/Card'
import Pill from '../components/elements/Pill'
import FormCampusSelect from '../components/forms/FormCampusSelect'
import FormWrapperGray from '../components/forms/FormWrapperGray'
import LazyImage from '../components/reusables/LazyImage'
import BlogHero from '../components/specific-to-template/blog/BlogHero'
import BlogNav from '../components/specific-to-template/blog/BlogNav'
import FormCTA from '../components/specific-to-template/blog/FormCTA'
import { above, colors, fontSize } from '../style/design-system'
import sp from '../utils/filters/shortcodeParser'
import bls from '../utils/bls-citations'

const BlogSingle = ({ className, data, pageContext, path }) => {
  const { page, author } = data

  const pageURL = `https://www.ultimatemedical.edu${path}`

  const getRandomInt = (max) => Math.floor(Math.random() * Math.floor(max))

  const categoryString = ((categories) => {
    if (categories.length > 0) {
      return categories.reduce((acc, cur) => {
        if (!acc) {
          return (
            <Pill backgroundColor={colors.dev.green.g()} color={colors.dev.green.a()}>
              <Link to={`/blog/category/${slugify(cur.toLowerCase())}/`}>{cur}</Link>
            </Pill>
          )
        }
        return (
          <>
            {acc}
            <Pill backgroundColor={colors.dev.green.g()} color={colors.dev.green.a()}>
              <Link to={`/blog/category/${slugify(cur.toLowerCase())}/`}>{cur}</Link>
            </Pill>
          </>
        )
      }, '')
    }
    return ''
  })(pageContext.categories)

  const relatedPosts = ((relatedPosts) => {
    if (relatedPosts.length < 4) {
      return relatedPosts
    }

    const indices = []
    do {
      const idx = getRandomInt(relatedPosts.length)
      if (indices.indexOf(idx) === -1) {
        indices.push(idx)
      }
    } while (indices.length < 3)

    const r = relatedPosts.filter((post, idx) => indices.indexOf(idx) !== -1)

    return r
  })(data.relatedPosts.edges)

  const formCTARef = createRef()
  const [CTAPlaced, SetCTAPlaced] = useState(false)
  const [hasBlsCitation, setHasBlsCitation] = useState(false)

  useEffect(() => {
    if (!CTAPlaced) {
      const targetP = document.querySelector('.page-content > p:nth-of-type(3)')
      if (targetP) {
        targetP.parentNode.insertBefore(formCTARef.current, targetP)
        SetCTAPlaced(true)
      }
    }
  }, [])

  useEffect(() => {
    const hasBls = page?.content && page?.content?.indexOf('Bureau of Labor Statistics') !== -1
    setHasBlsCitation(hasBls)
  }, [])

  return (
    <Layout>
      <SEO
        title={page.metaSeoTitle || page.metaPostTitle}
        description={page.metaSeoMetadesc}
        ogImage={page.featuredImage?.node?.localFile?.publicURL}
        useUMAAcronym={page.metaSeoTitleUseAcronym}>
        <script type="application/ld+json">
          {`{
              "@context": "http://schema.org",
              "@type": "Article",
              "name": "${page.metaPostTitle}",
              "headline": "${page.metaPostTitle}",
              "dateModified": "${page.modified}",
              "image": "${
                page.featuredImage?.node?.localFile?.publicURL ? page.featuredImage?.node?.localFile?.publicURL : ''
              }",
              "articleBody": "${page.content ? page.content.replace(/"/g, '\\"').replace(/\r?\n|\r/g, '') : ''}"
            }`}
        </script>
      </SEO>

      <BlogHero heroIsH1={false} />

      <div className={className}>
        <div className="everything-wrapper">
          <div className="nav-and-content">
            <div className="row">
              <div className="col-xl-4">
                <BlogNav currentPath={path} />
              </div>

              <div className="col-xl-8">
                <main>
                  <article className="blog-content">
                    <h1 className="mt-0 h2 blue">{htmr(page.metaPostTitle)}</h1>
                    {categoryString && (
                      <p className="category-string mb-0">
                        <small>In: {categoryString}</small>
                      </p>
                    )}
                    {page.modified && (
                      <p>
                        <small>Updated: {page.modified}</small>
                        {author?.metaProfileName === 'Gretchen Adams' && (
                          <>
                            <br />
                            <small>Written by Ultimate Medical Academy</small>
                          </>
                        )}
                      </p>
                    )}
                    {page.featuredImage && page.featuredImage.node?.localFile.childImageSharp.gatsbyImageData && (
                      <GatsbyImage
                        image={page.featuredImage.node.localFile.childImageSharp.gatsbyImageData}
                        className="mb-6"
                        loading="eager"
                        alt={page.metaPostTitle}
                      />
                    )}

                    <div className="page-content">
                      {page.content && htmr(sp(page.content))}
                      {hasBlsCitation && (
                        <p className="lh-1">
                          <small>{htmr(bls.global.usageDisclaimer)}</small>
                        </p>
                      )}
                    </div>
                    <FormCTA ref={formCTARef} />
                  </article>
                  <div className="py-6">
                    <FacebookShareButton url={pageURL} className="social m-2">
                      <FacebookIcon className="social__icon" />
                    </FacebookShareButton>
                    <TwitterShareButton url={pageURL} className="social m-2">
                      <TwitterIcon className="social__icon" />
                    </TwitterShareButton>
                    <LinkedinShareButton url={pageURL} className="social m-2">
                      <LinkedinIcon className="social__icon" />
                    </LinkedinShareButton>
                    <PinterestShareButton url={pageURL} className="social m-2">
                      <PinterestIcon className="social__icon" />
                    </PinterestShareButton>
                    <WhatsappShareButton url={pageURL} className="social m-2">
                      <WhatsappIcon className="social__icon" />
                    </WhatsappShareButton>
                    <TumblrShareButton url={pageURL} className="social m-2">
                      <TumblrIcon className="social__icon" />
                    </TumblrShareButton>
                    <RedditShareButton url={pageURL} className="social m-2">
                      <RedditIcon className="social__icon" />
                    </RedditShareButton>
                    <EmailShareButton url={pageURL} className="social m-2">
                      <EmailIcon className="social__icon" />
                    </EmailShareButton>
                  </div>
                </main>
              </div>
            </div>
          </div>

          <div id="blog-form" className="mt-7">
            <FormWrapperGray>
              <FormCampusSelect includeUserErrorModal />
            </FormWrapperGray>
          </div>

          <div className="aside-wrapper mt-md-11 mt-xl-0">
            <aside>
              {author && author?.metaProfileName !== 'Gretchen Adams' && (
                <div className="about-the-author">
                  <h2 className="mt-0 mb-4 h3">About the Author</h2>
                  <Card className="p-4">
                    {author.metaProfilePic && (
                      <LazyImage
                        className="mb-4"
                        src={author.metaProfilePic}
                        alt={`${htmr(author.metaProfileName)} Profile Picture`}
                      />
                    )}
                    <p>{htmr(author.metaProfileShortBio)}</p>
                  </Card>
                </div>
              )}

              <div>
                <h2 className="h3">You Might Also Like</h2>
                {relatedPosts.map(({ node }, idx) => {
                  const alt = node.metaPostTitle
                  const image = node.featuredImage?.node.localFile.childImageSharp.gatsbyImageData
                  const featuredImage = alt && image ? { alt, image } : undefined
                  return featuredImage && node.uri !== path ? (
                    <BlogLinkWBgPicture
                      className="mb-6"
                      key={idx}
                      featuredImg={{ image: featuredImage.image, alt: featuredImage.alt }}
                      heading={node.metaPostTitle}
                      slug={node.uri}
                    />
                  ) : null
                })}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default styled(BlogSingle)`
  .mt-neg-4 {
    margin-top: -1rem;
  }
  .disclaimer {
    line-height: 1;
  }
  .social__icon {
    border-radius: 2px;
  }
  .everything-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    .nav-and-content {
      flex: 0 0 100%;
      order: 1;
      padding: 0 1.25rem;
      overflow: auto;
      ${above.sm`
        flex: 0 0 34rem;
      `}
      ${above.md`
        flex: 0 0 480px;
        padding-right: 1.25rem;
      `}
      ${above.lg`
        flex: 0 0 640px;
      `}
      ${above.xl`
        flex: 0 0 74%;
      `}
      ${above.xxl`
        flex: 0 0 1050px;
      `}
      ${above.xxxl`
        flex: 0 0 1080px;
      `}
    }
    #blog-form {
      flex: 1 1 100%;
      order: 2;
      ${above.md`
        order: 3;
      `}
    }
    .aside-wrapper {
      flex: 0 0 100%;
      order: 3;
      padding: 0 1.25rem;
      ${above.sm`
        flex: 0 0 34rem;
      `}
      ${above.md`
        flex: 0 0 240px;
        order: 2;
        padding-left: 1.25;
      `}
      ${above.lg`
        flex: 0 0 320px;
      `}
      ${above.xl`
        flex: 0 0 24%;
      `}
      ${above.xxl`
        flex: 0 0 350px;
      `}
      ${above.xxxl`
        flex: 0 0 360px;
      `}
      aside {
        font-size: ${fontSize.e};
      }
    }
  }
`

export const query = graphql`
  query ($id: String!, $author: Int!, $categories: [String]) {
    page: wpPost(id: { eq: $id }) {
      content
      modified(formatString: "dddd, MMMM D, YYYY @ h:mma")
      metaPostTitle
      metaSeoTitle
      metaSeoTitleUseAcronym
      metaSeoMetadesc
      uri
      featuredImage {
        node {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
            }
          }
        }
      }
    }
    author: wpUmaprofile(databaseId: { eq: $author }) {
      id
      metaProfileName
      metaProfilePic
      metaProfileShortBio
    }
    relatedPosts: allWpPost(
      limit: 10
      filter: { categories: { nodes: { elemMatch: { name: { in: $categories } } } } }
    ) {
      edges {
        node {
          uri
          metaPostTitle
          featuredImage {
            node {
              localFile {
                childImageSharp {
                  gatsbyImageData(placeholder: NONE, layout: CONSTRAINED, width: 325)
                }
              }
            }
          }
        }
      }
    }
  }
`
